import type { CategoryId } from '@shared/config/categories';
import type { MediaKind } from '@shared/api';

export type CampaignQuestion = {
  id: string;
  options: [string, string, string, string];
  correctOption: number;
  frameLabel: string;
  extraFrameLabel: string;
  mediaKind?: MediaKind;
};

export type CampaignChapter = {
  id: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
  questions: CampaignQuestion[];
};

const mediaKinds: Record<CategoryId, MediaKind> = { movies: 'film', series: 'series', games: 'game', anime: 'anime' };

const makeQuestions = (category: CategoryId, chapter: number, titles: string[]): CampaignQuestion[] =>
  titles.map((title, index) => {
    const others = titles.filter((candidate) => candidate !== title);
    const correctOption = index % 4;
    const options = others.slice(0, 3);
    options.splice(correctOption, 0, title);
    return {
      id: `${category}-${chapter}-${index + 1}`,
      options: options as [string, string, string, string],
      correctOption,
      mediaKind: mediaKinds[category],
      frameLabel: `Название: ${Array.from(title)
        .map((letter, position) => (position === 0 || letter === ' ' ? letter : '•'))
        .join('')}`,
      extraFrameLabel: `Другой кадр: ${Array.from(title)
        .map((letter, position) => (position < 3 || letter === ' ' ? letter : '•'))
        .join('')}`,
    };
  });

const chapter = (category: CategoryId, number: 1 | 2 | 3 | 4 | 5, id: string, titles: string[]): CampaignChapter => ({
  id,
  difficulty: number,
  questions: makeQuestions(category, number, titles),
});

export const campaignChapters: Record<CategoryId, CampaignChapter[]> = {
  movies: [
    chapter('movies', 1, 'first-scenes', ['Матрица', 'Титаник', 'Начало', 'Аватар', 'Интерстеллар']),
    chapter('movies', 2, 'film-worlds', ['Дюна', 'Барби', 'Джокер', 'Гладиатор', 'Оппенгеймер']),
  ],
  series: [
    chapter('series', 1, 'pilot', ['Друзья', 'Офис', 'Шерлок', 'Уэнсдей', 'Тьма']),
    chapter('series', 2, 'next-episode', ['Разделение', 'Аркейн', 'Локи', 'Медведь', 'Одни из нас']),
  ],
  games: [
    chapter('games', 1, 'start-game', ['Minecraft', 'Portal', 'Tetris', 'Hades', 'Celeste']),
    chapter('games', 2, 'second-level', ['BioShock', 'Stray', 'Hollow Knight', 'Control', 'Stardew Valley']),
  ],
  anime: [
    chapter('anime', 1, 'first-episode', [
      'Наруто',
      'Атака титанов',
      'Ван-Пис',
      'Тетрадь смерти',
      'Клинок, рассекающий демонов',
    ]),
    chapter('anime', 2, 'anime-worlds', [
      'Евангелион',
      'Ковбой Бибоп',
      'Стальной алхимик',
      'Магическая битва',
      'Семья шпиона',
    ]),
  ],
};

export const getCampaignChapter = (categoryId: CategoryId, chapterId: string) =>
  campaignChapters[categoryId].find(({ id }) => id === chapterId);
